import { z } from 'zod';
import { LINK_ID_PATTERN } from './links';

/**
 * linkId 请求字段(telemetry 入口统一用)。可缺省(无 linkId 的访问合法),但一旦
 * 带了 linkId 就必须符合形状:带非法 linkId(如爬虫的 `data:,`)的请求直接 schema
 * 校验失败 → 路由返回 400、不落库 —— 这类请求整条丢弃,不进 telemetry。
 * LINK_ID_PATTERN 已含 1..64 长度约束。
 */
export const linkIdField = z.string().regex(LINK_ID_PATTERN).optional();

export const clientSignalsSchema = z.object({
  // Navigator
  userAgent: z.string(),
  platform: z.string().optional(),
  languages: z.array(z.string()).default([]),
  hardwareConcurrency: z.number().optional(),
  deviceMemory: z.number().optional(),
  pluginsCount: z.number().optional(),
  webdriver: z.boolean().optional(),
  // Screen
  screenWidth: z.number().optional(),
  screenHeight: z.number().optional(),
  innerWidth: z.number().optional(),
  innerHeight: z.number().optional(),
  colorDepth: z.number().optional(),
  devicePixelRatio: z.number().optional(),
  // Locale
  timezone: z.string().optional(),
  // Fingerprints (hash)
  canvasHash: z.string().optional(),
  webglHash: z.string().optional(),
  audioHash: z.string().optional(),
  fontsHash: z.string().optional(),
  // Touch / pointer
  maxTouchPoints: z.number().optional(),
  // Headless markers
  hasChromeRuntime: z.boolean().optional(),
  hasNotificationPermissionMismatch: z.boolean().optional(),
  // Behavioral
  dwellMs: z.number().default(0),
  mouseEvents: z.number().default(0),
  touchEvents: z.number().default(0),
  scrollEvents: z.number().default(0),
  keyEvents: z.number().default(0),
  pointerType: z.string().optional(),
  visibilityChanges: z.number().default(0),
  // Motion sensors
  motionSamples: z.number().default(0),
  motionMaxDelta: z.number().default(0),
  orientationSamples: z.number().default(0),
  // Battery
  hasBattery: z.boolean().optional(),
  batteryCharging: z.boolean().optional(),
  batteryLevel: z.number().optional(),
  // Network connection
  connectionType: z.string().optional(),
  connectionDownlink: z.number().optional(),
  connectionRtt: z.number().optional(),
  saveData: z.boolean().optional(),
  // Media devices
  mediaInputs: z.number().optional(),
  mediaOutputs: z.number().optional(),
  mediaCameras: z.number().optional(),
  // Storage
  hasLocalStorage: z.boolean().optional(),
  hasSessionStorage: z.boolean().optional(),
  hasIndexedDB: z.boolean().optional(),
  // Platform consistency
  platformUaMatch: z.boolean().optional(),
  // Performance jitter
  perfNowJitter: z.number().optional(),
  // Driver / anti-detect markers
  driverMarkers: z.array(z.string()).default([]),
  nativeFnTampered: z.array(z.string()).default([]),
  // WebGPU
  webgpuVendor: z.string().optional(),
  webgpuArchitecture: z.string().optional(),
  // Speech voices
  speechVoicesCount: z.number().optional(),
  // CSS feature support snapshot
  cssFeatures: z.record(z.string(), z.boolean()).optional(),
  // WebAuthn platform authenticator
  hasPlatformAuthenticator: z.boolean().optional(),
  // 指纹收集期间至少一个异步探测超时(恶劣 in-app webview:如 iOS TikTok 的
  // OfflineAudioContext 永不 resolve)。verify 仍带已收集的部分信号照常判定,
  // 该 flag 让降级访问在 audits.reasons 里可辨(observation-only 规则,不改判定)。
  signalCollectTimedOut: z.boolean().optional(),
  // Touch event physical properties (on challenge tap)
  touchForce: z.number().optional(),
  touchRadiusX: z.number().optional(),
  touchRadiusY: z.number().optional(),
  // Mouse trajectory
  mouseSampleCount: z.number().optional(),
  mouseAngleStdDev: z.number().optional(),
  mouseSpeedStdDev: z.number().optional(),
  // Client hints (filled server-side from headers, not by browser JS)
  clientHintUa: z.string().optional(),
  clientHintMobile: z.string().optional(),
  clientHintPlatform: z.string().optional(),
  // Mobile-specific
  webglRendererSummary: z.string().optional(), // lowercased vendor+renderer fragment, for desktop-gpu detection
  hoverNone: z.boolean().optional(), // CSS @media (hover: none)
  pointerCoarse: z.boolean().optional(), // CSS @media (pointer: coarse)
  hasTouchEventCtor: z.boolean().optional(), // 'ontouchstart' in window
  hasVibrate: z.boolean().optional(), // navigator.vibrate exists
  orientationType: z.string().optional(), // screen.orientation.type
  orientationAngle: z.number().optional(), // screen.orientation.angle
  visualViewportScale: z.number().optional(), // visualViewport.scale
  iosStandalone: z.boolean().optional(), // navigator.standalone
  // Active challenge
  challengePassed: z.boolean().default(false),
  challengeMs: z.number().default(0),
  challengeKind: z.enum(['click', 'touch', 'timeout', 'none']).default('none'),
  // In-app webview hint (feeds the notInAppWebview rule + isAdPlatformWebview).
  // Name kept for client back-compat; semantics are "in an ad-platform webview".
  isTikTokWebview: z.boolean().optional(),
  // Detected in-app browser app key (tiktok/facebook/instagram/feishu/wechat/…); '' = not in-app
  inAppBrowser: z.string().optional(),
  // --- New signals (v2) ---
  // rAF frame timing jitter (stddev of rAF intervals in ms; headless is unnaturally flat)
  rafJitter: z.number().optional(),
  // Canvas noise detection: render twice, compare hashes — anti-fingerprint tools inject noise
  canvasStable: z.boolean().optional(),
  // Scroll behavior
  scrollSpeedStdDev: z.number().optional(),
  scrollSamples: z.number().default(0),
  // Permission API states
  permissionCamera: z.string().optional(),
  permissionMicrophone: z.string().optional(),
  permissionGeolocation: z.string().optional(),
  // Clipboard API
  hasClipboard: z.boolean().optional(),
  // PDF viewer / MIME types
  pdfViewerEnabled: z.boolean().optional(),
  mimeTypesCount: z.number().optional(),
  // Click position entropy (stddev of click x,y — bots click center)
  clickPositionStdDev: z.number().optional(),
  clickSamples: z.number().default(0),
  // Probe-derived cross-check (filled by client after receiving probe response)
  probeCountry: z.string().optional(),
  probeTimezoneMatch: z.boolean().optional(),
  probeLanguageMatch: z.boolean().optional(),
  probeVerifyIpMatch: z.boolean().optional(),
  // --- v3: BotD (FingerprintJS open-source) client-side verdict ---
  // Android-only Google Pay probe; status separates absent payment method from API failures.
  googlePayReady: z.boolean().optional(),
  googlePayStatus: z
    .enum(['present', 'absent', 'rejected', 'sdk-unavailable', 'timeout'])
    .optional(),
  googlePayError: z.string().max(200).optional(),

  // Both undefined when BotD failed to load / timed out (fail-open: no penalty).
  botdBot: z.boolean().optional(),
  botdKind: z.string().optional(),
  // --- v5: edge session lifecycle ---
  // true = 边缘注入的 __TC_SID 缺失,客户端自铸了 sid(缓存 HTML / 静态直出 / dev)。
  sidFallback: z.boolean().optional(),
});

export type ClientSignals = z.infer<typeof clientSignalsSchema>;

export const interactionEventKindSchema = z.enum([
  'pageView',
  'probeRequested',
  'probeDone',
  'fingerprintStart',
  'fingerprintDone',
  'challengeShown',
  'challengeClicked',
  'challengeTouched',
  'challengeTimeout',
  'firstMouseMove',
  'firstTouch',
  'firstScroll',
  'firstKey',
  'visibilityChange',
  'motionFirstSample',
  'orientationFirstSample',
  'verifyRequested',
  'redirectStarted',
  'redirectAttempt',
  'redirectAppSwitched',
  'redirectFallback',
  'redirectGiveUp',
  'redirectInline',
  // 已记 fallback 之后仍检测到 app 切换 —— 即「晚切」(Chrome 起来了但超出主窗口),
  // 用于校准真实唤起率,把被 fallback 误计的成功捞回来。
  'redirectLateSwitch',
]);
export type InteractionEventKind = z.infer<typeof interactionEventKindSchema>;

export const interactionEventSchema = z.object({
  kind: interactionEventKindSchema,
  tsOffsetMs: z.number().int().min(0).max(3_600_000),
  data: z.record(z.string(), z.unknown()).optional(),
});
export type InteractionEvent = z.infer<typeof interactionEventSchema>;

export const verifyRequestSchema = z.object({
  sessionId: z.string().min(8).max(64),
  linkId: linkIdField,
  // Persistent first-party device id (localStorage + cookie). Distinct from the
  // per-page-load sessionId — used to detect returning + cross-link devices.
  deviceId: z.string().min(8).max(64).optional(),
  ttclid: z.string().optional(),
  fbclid: z.string().optional(),
  gclid: z.string().optional(),
  referer: z.string().optional(),
  pageHref: z.string().optional(),
  // Client bundle release version (repo-root VERSION file), stamped onto the audit.
  version: z.string().max(32).optional(),
  signals: clientSignalsSchema,
  events: z.array(interactionEventSchema).max(200).default([]),
  // 签名令牌(无状态交叉校验,见 shared/token.ts)。可选 —— 旧客户端/缓存 HTML/
  // bfcache 不带,引擎按"缺 token"信号降级处理,不拒绝请求。
  edgeToken: z.string().max(2048).optional(),
  probeToken: z.string().max(2048).optional(),
});

export const beaconRequestSchema = z.object({
  sessionId: z.string().min(8).max(64),
  linkId: linkIdField,
  deviceId: z.string().min(8).max(64).optional(),
  kind: z.enum(['pageView', 'pageHide']),
  ttclid: z.string().optional(),
  fbclid: z.string().optional(),
  gclid: z.string().optional(),
  referer: z.string().optional(),
  pageHref: z.string().optional(),
  partialSignals: clientSignalsSchema.partial().optional(),
});

export type BeaconRequest = z.infer<typeof beaconRequestSchema>;

export const eventsBatchSchema = z.object({
  sessionId: z.string().min(8).max(64),
  linkId: linkIdField,
  events: z.array(interactionEventSchema).min(1).max(100),
});

export type EventsBatch = z.infer<typeof eventsBatchSchema>;

export type VerifyRequest = z.infer<typeof verifyRequestSchema>;

export const verifyResponseSchema = z.object({
  decision: z.enum(['pass', 'block']),
  redirect: z.string().url(),
  auditId: z.string(),
  score: z.number(),
});

export type VerifyResponse = z.infer<typeof verifyResponseSchema>;

// --- Probe endpoint schemas ---
export const probeRequestSchema = z.object({
  sessionId: z.string().min(8).max(64),
  linkId: linkIdField,
  ttclid: z.string().optional(),
  fbclid: z.string().optional(),
  gclid: z.string().optional(),
  pageHref: z.string().optional(),
  referer: z.string().optional(),
});
export type ProbeRequest = z.infer<typeof probeRequestSchema>;

export const probeResponseSchema = z.object({
  probeId: z.string(),
  detectedCountry: z.string().optional(),
  detectedCity: z.string().optional(),
  detectedRegion: z.string().optional(),
  isDatacenterIp: z.boolean(),
  tlsVersion: z.string().optional(),
  httpVersion: z.string().optional(),
  ja4: z.string().optional(),
  colo: z.string().optional(),
  expectedTimezones: z.array(z.string()).default([]),
  expectedLanguages: z.array(z.string()).default([]),
  ip: z.string().optional(),
  asn: z.number().optional(),
  asnOrg: z.string().optional(),
  serverTs: z.number(),
  // 签名 probe token:verify 时带回,worker 验签后做交叉校验(替代存储回读)。
  probeToken: z.string().optional(),
});
export type ProbeResponse = z.infer<typeof probeResponseSchema>;
